import { useLayoutEffect } from 'react';

import { Accent, getAccentFromColor } from 'utils/steps';

const ACCENT_ATTRIBUTE = 'data-accent';

/**
 * Applies the accent to the document root. Blue is the default, so the
 * attribute is removed for it — this drives the accent CSS variables
 * (see globals.css).
 */
const applyAccent = (accent: Accent) => {
  const root = document.documentElement;

  if (accent === Accent.Blue) {
    root.removeAttribute(ACCENT_ATTRIBUTE);
  } else {
    root.setAttribute(ACCENT_ATTRIBUTE, accent);
  }
};

/**
 * Syncs the app accent (blue/purple) with a course step color. Pass the first
 * step's color; the accent resets to the default on unmount (i.e. when leaving
 * the authenticated course area).
 */
export const useAccentSync = (color?: string) => {
  const accent = getAccentFromColor(color);

  useLayoutEffect(() => {
    applyAccent(accent);

    return () => {
      applyAccent(Accent.Blue);
    };
  }, [accent]);
};

/**
 * Reads the active accent (blue/purple) from the `data-accent` attribute set
 * by useAccentSync. Use it to pick theme-specific assets (e.g. icons).
 */
export const useAccent = (): Accent =>
  document.documentElement.getAttribute(ACCENT_ATTRIBUTE) === Accent.Purple
    ? Accent.Purple
    : Accent.Blue;
